import { Box, Spinner, Tooltip, useDisclosure, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CheckIcon } from '@chakra-ui/icons';
import { toast } from 'react-toastify';

import useServicesAdd from 'hooks/useServicesAdd';

import { ExclamationMarkIcon } from 'components/icons/ExclamationMarkIcon';
import { Button, Card, CardBody, Text } from 'components/v4/';
import Image from 'components/v4/Image';
import walkthroughIds from 'pages/services/walkthroughIds';
import ConfirmationModal from './ConfirmationModal';

import path from '@/routes/constants';
import { PlatformEnums } from 'utils/platforms';
import createService from 'API/Service/createService';
import getCompanyPlatforms from 'API/Company/getCompanyPlatforms';
import {
    ACS,
    EIGHTBYEIGHT,
    VENDOR_DEFINED,
    OPERATOR_PLATFORM,
} from '../constants';

const ServicesAddCard = ({
    serviceWizard,
    serviceProfile,
    id,
    isAdded,
    isButtonDisabled,
    operatorPlatformName,
    hasOperatorPlatform,
    operatorPlatformServiceProfile,
    isAutoConfirm = false,
    isNewDashboard = false,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    // (3135) "+ Add now" on the dashboard lands here with this service's
    // confirmation already open, once, unless the card can't be added.
    const hasAutoConfirmedRef = useRef(false);
    useEffect(() => {
        if (!isAutoConfirm || hasAutoConfirmedRef.current) return;
        if (isAdded || isButtonDisabled) return;
        hasAutoConfirmedRef.current = true;
        onOpen();
    }, [isAutoConfirm, isAdded, isButtonDisabled, onOpen]);

    // Close the confirmation if the service turns out to be added already.
    useEffect(() => {
        if (isAdded) onClose();
    }, [isAdded, onClose]);
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [isAdding, setIsAdding] = useState(false);
    const [isAddingOperatorPlatform, setIsAddingOperatorPlatform] =
        useState(false);
    const acsPath = `${path.SERVICES_AZURE_CONNECT_SERVICE}/${path.CREATE}`;
    const isServiceProfileSelected =
        serviceWizard?.selectedServiceProfile?.serviceType ===
        serviceProfile?.serviceType;
    const isCurrentServiceAdding =
        serviceWizard?.isCreatingService && isServiceProfileSelected;
    const isCreatingServiceError =
        isServiceProfileSelected &&
        Boolean(serviceWizard.hasCreateServiceError);

    const { ufNewServiceProfileCard } = walkthroughIds.dashboardServices;

    const vendorDefined = serviceProfile?.serviceType === VENDOR_DEFINED;
    const isOperatorPlatformChild =
        vendorDefined &&
        serviceProfile?.serviceDefinition?.platformID ===
            PlatformEnums.OPERATOR_PLATFORM;

    const {
        cardBackground,
        cardBorder,
        pointerEvents,
        filterEffect,
        opacity,
        imageSrc,
        serviceName,
        serviceDescription,
        showError,
        setShowError,
    } = useServicesAdd({
        darkMode,
        isAdding,
        isServiceProfileSelected,
        isCreatingServiceError,
        isCurrentServiceAdding,
        serviceProfile,
        serviceWizard,
        vendorDefined,
    });

    const handleCancel = () => {
        serviceWizard.setHasCreateServiceError(null);
        serviceWizard.setIsCreatingService(false);
        setShowError(false);
        setIsAdding(false);
    };

    const onClick = () => {
        switch (serviceProfile.serviceType) {
            case ACS:
                navigate(acsPath);
                break;
            default:
                serviceWizard.createService(serviceProfile);
                break;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onClose();
        if (isAdded) return;
        // (3082/3083) Vendor, operator and 8x8 services are confirmed here
        // first, then continue to their own add screens.
        if (
            vendorDefined &&
            serviceProfile?.serviceType !== OPERATOR_PLATFORM
        ) {
            serviceWizard.setSelectedServiceProfile(serviceProfile);
            serviceWizard.setFlow('VendorDefined');
            return;
        }
        if (serviceProfile?.serviceType === EIGHTBYEIGHT) {
            serviceWizard.setSelectedServiceProfile(serviceProfile);
            serviceWizard.setFlow('AddEightByEight');
            return;
        }
        onClick(e);
        setIsAdding(true);
        if (!isCreatingServiceError) {
            queryClient.invalidateQueries({
                queryKey: ['services', `company: ${currentCompany}`],
            });
        }
    };

    const handleAddClick = async () => {
        // Operator Platform must exist before a child service can be added.
        // Rather than blocking the click (or only adding Operator Platform
        // and making the user click again), add it automatically here and
        // then continue straight into adding this child, same as if the
        // parent had already existed.
        if (isOperatorPlatformChild && !hasOperatorPlatform) {
            if (!operatorPlatformServiceProfile || isAddingOperatorPlatform) {
                return;
            }

            setIsAddingOperatorPlatform(true);
            const response = await createService({
                companyId: currentCompany,
                serviceType: OPERATOR_PLATFORM,
                name: 'Operator Platform',
            });
            setIsAddingOperatorPlatform(false);

            if (response?.response?.data?.message) {
                toast.error(response.response.data.message);
                return;
            }

            toast.success(`${operatorPlatformName} added automatically.`);
            queryClient.invalidateQueries({
                queryKey: ['services', `company: ${currentCompany}`],
            });
            const platforms = await getCompanyPlatforms(
                requiredScope,
                currentCompany,
            );
            dispatch({
                type: 'CHANGE_CURRENT_COMPANY_PLATFORMS',
                payload: platforms,
            });
        }
        onOpen();
    };

    // Render error state content
    const renderErrorState = () => {
        return (
            <>
                <VStack align="flex-start" spacing={3} width="100%">
                    <Image
                        className="rounded-xl"
                        src={imageSrc}
                        boxSize="50px"
                        objectFit="contain"
                    />
                    <Text fontWeight="bold">{serviceName}</Text>
                    <Text color="red.700" fontSize="sm">
                        <ExclamationMarkIcon />{' '}
                        {serviceWizard?.hasCreateServiceError}
                    </Text>
                </VStack>
                <Button
                    sx={{ width: '100%' }}
                    colorScheme="red"
                    variant="outline"
                    onClick={handleCancel}>
                    Cancel
                </Button>
            </>
        );
    };

    // Render normal state content
    const renderNormalState = () => {
        const hasTooltip = serviceDescription.long;

        return (
            <>
                <VStack align="flex-start" spacing={3} width="100%">
                    <Image
                        className="rounded-xl"
                        src={imageSrc}
                        boxSize="50px"
                        objectFit="contain"
                    />
                    <Text fontWeight="bold">{serviceName}</Text>
                    <Text>{serviceDescription.short}</Text>
                </VStack>
                <Box width="100%">
                    <Tooltip
                        hasArrow
                        label={
                            hasTooltip
                                ? serviceDescription.long
                                : `${operatorPlatformName} must be added before ${serviceName} can be added`
                        }
                        isDisabled={!hasTooltip && !isButtonDisabled}>
                        <Box width="100%">
                            {isAdded ? (
                                <Button
                                    sx={{ width: '100%' }}
                                    variant="outline"
                                    colorScheme="green"
                                    bg={darkMode ? 'transparent' : 'green.100'}
                                    borderColor={
                                        darkMode ? 'green.300' : 'green.500'
                                    }
                                    color={darkMode ? 'green.200' : 'green.700'}
                                    leftIcon={<CheckIcon />}
                                    isDisabled
                                    _disabled={{
                                        opacity: 1,
                                        cursor: 'not-allowed',
                                    }}
                                    _hover={{
                                        bg: darkMode
                                            ? 'transparent'
                                            : 'green.100',
                                    }}>
                                    Added
                                </Button>
                            ) : (
                                <Button
                                    sx={{ width: '100%' }}
                                    onClick={handleAddClick}
                                    isLoading={isAddingOperatorPlatform}
                                    isDisabled={isButtonDisabled}>
                                    + Add
                                </Button>
                            )}
                        </Box>
                    </Tooltip>
                </Box>
            </>
        );
    };

    return (
        <>
            <Card
                bg={cardBackground}
                border={cardBorder}
                borderRadius={10}
                pointerEvents={pointerEvents}
                key={id}
                data-walkthroughid={`${ufNewServiceProfileCard}/${id}`}
                sx={{
                    filter: filterEffect,
                    opacity: opacity,
                    transition: 'all 0.3s ease',
                }}>
                <CardBody position="relative" height="100%">
                    {isServiceProfileSelected &&
                        isAdding &&
                        (!showError ||
                            !serviceWizard.hasCreateServiceError) && (
                            <Box
                                pointerEvents="none"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    position: 'absolute',
                                    width: '100%',
                                    left: '0',
                                    top: '0',
                                    zIndex: 1,
                                }}>
                                <Spinner
                                    thickness="4px"
                                    emptyColor="gray.200"
                                    color="brand.500"
                                    size="xl"
                                />
                            </Box>
                        )}
                    <VStack
                        align="flex-start"
                        spacing={3}
                        width="100%"
                        height="100%"
                        justifyContent="space-between">
                        {showError ? renderErrorState() : renderNormalState()}
                    </VStack>
                </CardBody>
            </Card>
            <ConfirmationModal
                serviceProfile={serviceProfile}
                imageSrc={imageSrc}
                serviceName={serviceName}
                description={serviceDescription.short}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                handleSubmit={handleSubmit}
                isNewDashboard={isNewDashboard}
            />
        </>
    );
};

export default ServicesAddCard;
